import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useEventsAdminDemo } from "@app/lib/events-admin-demo-context";
import { GET_ORGANIZERS } from "@app/graphql/events-admin.queries";
import {
  CloseIcon,
  ExternalLinkIcon,
  MessageIcon,
  UserCircleIcon,
  UserGroupIcon,
} from "@app/lib/events-admin-icons";
import { initialsForName } from "@app/lib/events-admin-utils";
import { Controller } from "react-hook-form";
import { Link, useNavigate } from "react-router";
import type { z } from "zod";
import {
  Dialog,
  Modal,
  ModalOverlay,
  Button,
  ValidatedForm,
  ValidatedInput,
  TextAreaBase,
} from "@its-engineering-and-development/ui-foundation/components";
import {
  createFormSchema,
  schemas,
  useValidatedForm,
} from "@its-engineering-and-development/ui-foundation/utils";
import "./organizers-page.css";

const inviteOrganizerSchema = createFormSchema({
  name: schemas.sanitizedRequiredString,
  email: schemas.email,
});

const messageUserSchema = createFormSchema({
  subject: schemas.sanitizedRequiredString,
  message: schemas.sanitizedRequiredString,
});

export function OrganizersPage() {
  const navigate = useNavigate();
  const { organizers: localOrganizers, addOrganizer } = useEventsAdminDemo();

  // GraphQL: fetch organizers from mock server
  const { data: gqlData } = useQuery<{ organizers: typeof localOrganizers }>(
    GET_ORGANIZERS,
    { fetchPolicy: "network-only" },
  );
  const organizers = gqlData?.organizers ?? localOrganizers;

  const [selectedOrganizerId, setSelectedOrganizerId] = useState<string | null>(
    null,
  );
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [messageSentConfirm, setMessageSentConfirm] = useState(false);

  const inviteForm = useValidatedForm(inviteOrganizerSchema, {
    defaultValues: { name: "", email: "" },
  });

  const messageForm = useValidatedForm(messageUserSchema, {
    defaultValues: { subject: "", message: "" },
  });

  const handleInviteOrganizer = (
    values: z.infer<typeof inviteOrganizerSchema>,
  ) => {
    addOrganizer(values);
    inviteForm.reset();
    setIsInviteOpen(false);
  };

  const handleSendMessage = (_values: z.infer<typeof messageUserSchema>) => {
    messageForm.reset();
    setIsMessageOpen(false);
    setMessageSentConfirm(true);
    setTimeout(() => setMessageSentConfirm(false), 2000);
  };

  useEffect(() => {
    if (!selectedOrganizerId && organizers.length > 0) {
      setSelectedOrganizerId(organizers[0].id);
    }
  }, [organizers, selectedOrganizerId]);

  const selectedOrganizer = useMemo(
    () =>
      organizers.find((organizer) => organizer.id === selectedOrganizerId) ??
      organizers[0],
    [organizers, selectedOrganizerId],
  );

  return (
    <section className="events-page events-page--stack organizers-page">
      <header className="events-page__hero">
        <div>
          <h1 className="events-page__title">Organizers</h1>
          <p className="events-page__subtitle">
            Manage and monitor travel organizers active on the platform.
          </p>
        </div>

        <Button
          size="lg"
          color="primary"
          className="events-action-button"
          onPress={() => setIsInviteOpen(true)}
        >
          <UserGroupIcon className="size-4" />
          Invite New Organizer
        </Button>
      </header>

      <div className="events-organizers-grid">
        <article className="events-surface events-surface--table">
          <table className="events-table">
            <thead>
              <tr>
                <th>Name & ID</th>
                <th>Events</th>
                <th>Travelers</th>
                <th>Booking Rate</th>
                <th>Message</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {organizers.map((organizer) => (
                <tr
                  key={organizer.id}
                  className={`events-table__clickable-row ${
                    selectedOrganizer?.id === organizer.id
                      ? "is-highlighted"
                      : ""
                  }`.trim()}
                  onClick={() => setSelectedOrganizerId(organizer.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedOrganizerId(organizer.id);
                    }
                  }}
                >
                  <td data-label="Name & ID">
                    <strong className="events-table__primary-link">
                      {organizer.name}
                    </strong>
                    <span className="events-table__caption">
                      {organizer.id}
                    </span>
                  </td>
                  <td data-label="Events">{organizer.events}</td>
                  <td data-label="Travelers">{organizer.travelers}</td>
                  <td data-label="Booking Rate">
                    <span
                      className={
                        organizer.bookingRate >= 80
                          ? "events-tone events-tone--success"
                          : "events-tone events-tone--warning"
                      }
                    >
                      {organizer.bookingRate}%
                    </span>
                  </td>
                  <td data-label="Message">
                    <MessageIcon className="size-5 text-[#a6afc6]" />
                  </td>
                  <td data-label="Open">
                    <ExternalLinkIcon className="size-4 text-[#a6afc6]" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        {selectedOrganizer ? (
          <aside className="events-surface events-surface--profile">
            <header className="events-profile-card__header">
              <div className="events-profile-card__identity">
                <div className="events-profile-card__avatar">
                  {initialsForName(selectedOrganizer.name)}
                </div>
                <div>
                  <h2>{selectedOrganizer.name}</h2>
                  <p>{selectedOrganizer.email}</p>
                </div>
              </div>
              <button
                type="button"
                className="events-profile-card__more"
                onClick={() =>
                  window.open(
                    `mailto:${selectedOrganizer.email}?subject=Regarding organizer ${selectedOrganizer.id}`,
                    "_blank",
                  )
                }
              >
                ...
              </button>
            </header>

            <div className="events-profile-card__stats">
              <article>
                <span>Lifetime Bookings</span>
                <strong>
                  {selectedOrganizer.lifetimeBookings.toLocaleString()}
                </strong>
              </article>
              <article>
                <span>Engagement</span>
                <strong>{selectedOrganizer.engagement}</strong>
              </article>
            </div>

            <section className="events-profile-card__programs">
              <header>
                <UserCircleIcon className="size-4" />
                <h3>Active Management</h3>
              </header>

              {selectedOrganizer.programs.map((program) => (
                <Link
                  key={program.id}
                  to={`/organizers/${selectedOrganizer.id}/groups/${program.eventId}`}
                  className="events-profile-card__program"
                >
                  <div>
                    <strong>{program.name}</strong>
                    <p>{program.endsOn}</p>
                  </div>
                  <ExternalLinkIcon className="size-4" />
                </Link>
              ))}
            </section>

            <footer className="events-profile-card__actions">
              <Button
                size="lg"
                color="primary"
                className="events-solid-button"
                onPress={() => {
                  messageForm.reset({
                    subject: `Message for ${selectedOrganizer.name}`,
                    message: "",
                  });
                  setIsMessageOpen(true);
                }}
              >
                {messageSentConfirm
                  ? "Message Sent!"
                  : `Message ${selectedOrganizer.name.split(" ")[0]}`}
              </Button>
              <Button
                size="lg"
                color="secondary"
                className="events-outline-button"
                onPress={() =>
                  navigate(
                    `/organizers/${selectedOrganizer.id}/groups/${selectedOrganizer.programs[0]?.eventId ?? ""}`,
                  )
                }
              >
                Review Audit
              </Button>
            </footer>
          </aside>
        ) : null}
      </div>

      <ModalOverlay isOpen={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <Modal className="events-modal events-modal--compact">
          <Dialog className="events-dialog" aria-label="Invite new organizer">
            <div className="events-modal__header">
              <h3>Invite New Organizer</h3>
              <button
                type="button"
                className="events-dialog__close"
                onClick={() => setIsInviteOpen(false)}
                aria-label="Close modal"
              >
                <CloseIcon className="size-4" />
              </button>
            </div>

            <ValidatedForm
              form={inviteForm}
              onSubmit={handleInviteOrganizer}
              className="events-form-stack"
            >
              <ValidatedInput
                form={inviteForm}
                name="name"
                label="Full Name"
                placeholder="Enter organizer's full name"
              />
              <ValidatedInput
                form={inviteForm}
                name="email"
                label="Email Address"
                placeholder="organizer@company.com"
                type="email"
              />

              <div className="events-modal__footer">
                <Button
                  size="lg"
                  color="primary"
                  className="events-solid-button"
                  type="submit"
                >
                  Send Invite
                </Button>
                <Button
                  size="lg"
                  color="secondary"
                  className="events-outline-button"
                  onPress={() => setIsInviteOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </ValidatedForm>
          </Dialog>
        </Modal>
      </ModalOverlay>

      <ModalOverlay isOpen={isMessageOpen} onOpenChange={setIsMessageOpen}>
        <Modal className="events-modal events-modal--compact">
          <Dialog className="events-dialog" aria-label="Send message">
            <div className="events-modal__header">
              <h3>Message {selectedOrganizer?.name ?? "Organizer"}</h3>
              <button
                type="button"
                className="events-dialog__close"
                onClick={() => setIsMessageOpen(false)}
                aria-label="Close modal"
              >
                <CloseIcon className="size-4" />
              </button>
            </div>

            <ValidatedForm
              form={messageForm}
              onSubmit={handleSendMessage}
              className="events-form-stack"
            >
              <ValidatedInput
                form={messageForm}
                name="subject"
                label="Subject"
                placeholder="Message subject"
              />

              <Controller
                control={messageForm.control}
                name="message"
                render={({ field, fieldState }) => (
                  <label className="events-form-label">
                    Message
                    <TextAreaBase
                      rows={5}
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      placeholder="Type your message here..."
                      className={
                        fieldState.invalid ? "ring-error_subtle" : undefined
                      }
                    />
                    {fieldState.error ? (
                      <span className="events-form-error">
                        {fieldState.error.message}
                      </span>
                    ) : null}
                  </label>
                )}
              />

              <div className="events-modal__footer">
                <Button
                  size="lg"
                  color="primary"
                  className="events-solid-button"
                  type="submit"
                >
                  Send Message
                </Button>
                <Button
                  size="lg"
                  color="secondary"
                  className="events-outline-button"
                  onPress={() => setIsMessageOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </ValidatedForm>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </section>
  );
}
